import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  bpsToPercent,
  createSaSesEndpoint,
  deleteSaSesEndpoint,
  getSaSesEndpoints,
  getSaSesSettings,
  percentToBps,
  SaSesEndpoint,
  SaSesEndpointStatus,
  SaSesSettings,
  updateSaSesEndpoint,
  updateSaSesSettings,
} from "../../../helpers/super_admin_ops_helper";

const STATUS_COLORS: Record<SaSesEndpointStatus, string> = {
  active: "success",
  disabled: "secondary",
  blocked: "warning",
};

type EndpointFormState = {
  region: string;
  label: string;
  status: SaSesEndpointStatus;
  is_primary: boolean;
  quota_24h: string;
  max_per_second: string;
  safety_ceiling_percent: string;
  configuration_set: string;
  sort_order: string;
};

const emptyEndpointForm = (): EndpointFormState => ({
  region: "",
  label: "",
  status: "active",
  is_primary: false,
  quota_24h: "",
  max_per_second: "14",
  safety_ceiling_percent: "",
  configuration_set: "",
  sort_order: "0",
});

/**
 * Catálogo SES multi-região no SA (LAC-043).
 * Tenant nunca vê região/credenciais — só ops aqui.
 */
const SuperAdminSesEndpoints = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt-BR";

  const [settings, setSettings] = useState<SaSesSettings | null>(null);
  const [endpoints, setEndpoints] = useState<SaSesEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [safetyPercent, setSafetyPercent] = useState("95");
  const [reservedP0, setReservedP0] = useState("5000");
  const [accessKey, setAccessKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SaSesEndpoint | null>(null);
  const [form, setForm] = useState<EndpointFormState>(emptyEndpointForm);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [savingEndpoint, setSavingEndpoint] = useState(false);

  const [deleting, setDeleting] = useState<SaSesEndpoint | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  document.title = t("sa.ses.endpoints.documentTitle");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settingsRes, endpointsRes] = await Promise.all([
        getSaSesSettings(),
        getSaSesEndpoints(),
      ]);
      if (!settingsRes?.success || !endpointsRes?.success) {
        setError(t("sa.ses.error.load"));
        return;
      }
      setSettings(settingsRes.data);
      setSafetyPercent(String(bpsToPercent(settingsRes.data.safety_ceiling_bps)));
      setReservedP0(String(settingsRes.data.reserved_p0_daily ?? 5000));
      setAccessKey("");
      setSecretKey("");
      setEndpoints(endpointsRes.data || []);
    } catch (e: any) {
      setError(e?.message || t("sa.ses.error.load"));
      setSettings(null);
      setEndpoints([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const blockedCount = useMemo(
    () => endpoints.filter((e) => e.status === "blocked").length,
    [endpoints]
  );

  const saveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const percent = Number(safetyPercent);
      if (!Number.isFinite(percent) || percent < 10 || percent > 100) {
        toast.error(t("sa.ses.error.safetyRange"), { autoClose: 4000 });
        return;
      }
      const p0 = Number(reservedP0);
      if (!Number.isFinite(p0) || p0 < 0 || p0 > 50000) {
        toast.error(t("sa.ses.error.reservedP0Range"), { autoClose: 4000 });
        return;
      }
      const payload: {
        safety_ceiling_bps: number;
        reserved_p0_daily: number;
        aws_access_key_id?: string;
        aws_secret_access_key?: string;
      } = {
        safety_ceiling_bps: percentToBps(percent),
        reserved_p0_daily: Math.floor(p0),
      };
      // Vazio = não alterar secret/key no backend.
      if (accessKey.trim()) {
        payload.aws_access_key_id = accessKey.trim();
      }
      if (secretKey.trim()) {
        payload.aws_secret_access_key = secretKey.trim();
      }
      const res = await updateSaSesSettings(payload);
      toast.success(res?.message || t("sa.ses.toast.settingsSaved"), {
        autoClose: 2500,
      });
      setAccessKey("");
      setSecretKey("");
      setSettings(res.data);
      setSafetyPercent(String(bpsToPercent(res.data.safety_ceiling_bps)));
      setReservedP0(String(res.data.reserved_p0_daily ?? 5000));
    } catch (err: any) {
      toast.error(err?.message || t("sa.ses.error.saveSettings"), {
        autoClose: 4000,
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyEndpointForm());
    setFieldError(null);
    setModalOpen(true);
  };

  const openEdit = (row: SaSesEndpoint) => {
    setEditing(row);
    setForm({
      region: row.region,
      label: row.label,
      status: row.status,
      is_primary: row.is_primary,
      quota_24h: String(row.quota_24h),
      max_per_second: String(row.max_per_second),
      safety_ceiling_percent:
        row.safety_ceiling_bps != null
          ? String(bpsToPercent(row.safety_ceiling_bps))
          : "",
      configuration_set: row.configuration_set || "",
      sort_order: String(row.sort_order ?? 0),
    });
    setFieldError(null);
    setModalOpen(true);
  };

  const submitEndpoint = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingEndpoint(true);
    setFieldError(null);
    try {
      const quota = Number(form.quota_24h);
      const maxPs = Number(form.max_per_second || 14);
      const sortOrder = Number(form.sort_order || 0);
      if (!form.label.trim() || !Number.isFinite(quota) || quota < 1) {
        setFieldError(t("sa.ses.error.endpointFields"));
        return;
      }

      let safetyBps: number | null | undefined = undefined;
      if (form.safety_ceiling_percent.trim() === "") {
        safetyBps = editing ? null : undefined;
      } else {
        const pct = Number(form.safety_ceiling_percent);
        if (!Number.isFinite(pct) || pct < 10 || pct > 100) {
          setFieldError(t("sa.ses.error.safetyRange"));
          return;
        }
        safetyBps = percentToBps(pct);
      }

      if (editing) {
        const res = await updateSaSesEndpoint(editing.id, {
          label: form.label.trim(),
          status: form.status,
          is_primary: form.is_primary,
          quota_24h: quota,
          max_per_second: maxPs,
          safety_ceiling_bps: safetyBps === undefined ? null : safetyBps,
          configuration_set: form.configuration_set.trim() || null,
          sort_order: sortOrder,
        });
        toast.success(res?.message || t("sa.ses.toast.endpointUpdated"), {
          autoClose: 2500,
        });
      } else {
        if (!form.region.trim()) {
          setFieldError(t("sa.ses.error.regionRequired"));
          return;
        }
        const res = await createSaSesEndpoint({
          region: form.region.trim().toLowerCase(),
          label: form.label.trim(),
          status: form.status,
          is_primary: form.is_primary,
          quota_24h: quota,
          max_per_second: maxPs,
          safety_ceiling_bps: safetyBps,
          configuration_set: form.configuration_set.trim() || null,
          sort_order: sortOrder,
        });
        toast.success(res?.message || t("sa.ses.toast.endpointCreated"), {
          autoClose: 2500,
        });
      }
      setModalOpen(false);
      await load();
    } catch (err: any) {
      const details =
        err?.details?.region?.[0] ||
        err?.details?.label?.[0] ||
        err?.details?.quota_24h?.[0] ||
        err?.message;
      setFieldError(details || t("sa.ses.error.saveEndpoint"));
    } finally {
      setSavingEndpoint(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeletingBusy(true);
    try {
      const res = await deleteSaSesEndpoint(deleting.id);
      toast.success(res?.message || t("sa.ses.toast.endpointDeleted"), {
        autoClose: 2500,
      });
      setDeleting(null);
      await load();
    } catch (err: any) {
      toast.error(err?.message || t("sa.ses.error.delete"), {
        autoClose: 4000,
      });
    } finally {
      setDeletingBusy(false);
    }
  };

  const formatInt = (n: number) =>
    Number(n).toLocaleString(locale || undefined, { maximumFractionDigits: 0 });

  const formatUsage = (row: SaSesEndpoint) => {
    const pct = bpsToPercent(row.usage_24h_ratio_bps);
    return `${formatInt(row.usage_24h)} (${pct}%)`;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.ses.endpoints.title")}
          pageTitle={t("sa.menu.ops")}
        />
        <ToastContainer closeButton={false} limit={2} />

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button
            color="light"
            size="sm"
            tag={Link}
            to="/super-admin/ses/setup-guide"
          >
            {t("sa.ses.link.setupGuide")}
          </Button>
        </div>

        {error && (
          <Alert color="danger" className="d-flex justify-content-between gap-2">
            <span>{error}</span>
            <Button color="light" size="sm" onClick={() => load()}>
              {t("sa.ses.retry")}
            </Button>
          </Alert>
        )}

        {settings?.platform_send_frozen && (
          <Alert color="danger">{t("sa.ses.freezeAlert")}</Alert>
        )}

        {blockedCount > 0 && (
          <Alert color="warning">{t("sa.ses.blockedAlert", { count: blockedCount })}</Alert>
        )}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">{t("sa.ses.settings.title")}</h5>
              </CardHeader>
              <CardBody>
                {loading && !settings ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <Form onSubmit={saveSettings}>
                    <Row className="g-3">
                      <Col md={4}>
                        <Label for="sa-ses-safety">
                          {t("sa.ses.settings.safetyPercent")}
                        </Label>
                        <Input
                          id="sa-ses-safety"
                          type="number"
                          min={10}
                          max={100}
                          step={1}
                          value={safetyPercent}
                          onChange={(e) => setSafetyPercent(e.target.value)}
                          required
                        />
                        <p className="text-muted small mb-0 mt-1">
                          {t("sa.ses.settings.safetyHint")}
                        </p>
                      </Col>
                      <Col md={4}>
                        <Label for="sa-ses-p0">
                          {t("sa.ses.settings.reservedP0")}
                        </Label>
                        <Input
                          id="sa-ses-p0"
                          type="number"
                          min={0}
                          max={50000}
                          step={100}
                          value={reservedP0}
                          onChange={(e) => setReservedP0(e.target.value)}
                          required
                        />
                        <p className="text-muted small mb-0 mt-1">
                          {t("sa.ses.settings.reservedP0Hint", {
                            used: formatInt(settings?.p0_sent_today ?? 0),
                            remaining: formatInt(
                              settings?.p0_remaining_today ?? 0
                            ),
                          })}
                        </p>
                      </Col>
                      <Col md={4}>
                        <Label for="sa-ses-access">
                          {t("sa.ses.settings.accessKey")}
                        </Label>
                        <Input
                          id="sa-ses-access"
                          type="text"
                          autoComplete="off"
                          value={accessKey}
                          onChange={(e) => setAccessKey(e.target.value)}
                          placeholder={
                            settings?.has_access_key && settings.access_key_last4
                              ? t("sa.ses.settings.accessKeyPlaceholder", {
                                  last4: settings.access_key_last4,
                                })
                              : t("sa.ses.settings.accessKeyEmpty")
                          }
                        />
                      </Col>
                      <Col md={4}>
                        <Label for="sa-ses-secret">
                          {t("sa.ses.settings.secretKey")}
                        </Label>
                        <Input
                          id="sa-ses-secret"
                          type="password"
                          autoComplete="new-password"
                          value={secretKey}
                          onChange={(e) => setSecretKey(e.target.value)}
                          placeholder={
                            settings?.has_secret
                              ? t("sa.ses.settings.secretPlaceholder")
                              : t("sa.ses.settings.secretEmpty")
                          }
                        />
                      </Col>
                      <Col xs={12}>
                        <Label for="sa-ses-webhook">
                          {t("sa.ses.settings.webhookUrl")}
                        </Label>
                        <Input
                          id="sa-ses-webhook"
                          type="text"
                          readOnly
                          value={settings?.webhook_url || ""}
                          onFocus={(e) => e.target.select()}
                        />
                        <p className="text-muted small mb-0 mt-1">
                          {t("sa.ses.settings.webhookHint")}
                        </p>
                      </Col>
                      <Col xs={12}>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={savingSettings}
                        >
                          {savingSettings ? (
                            <Spinner size="sm" />
                          ) : (
                            t("sa.ses.settings.save")
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <h5 className="card-title mb-0">
                  {t("sa.ses.endpoints.tableTitle")}
                </h5>
                <Button color="primary" size="sm" onClick={openCreate}>
                  {t("sa.ses.endpoints.add")}
                </Button>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : endpoints.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-3">{t("sa.ses.endpoints.empty")}</p>
                    <Button color="primary" onClick={openCreate}>
                      {t("sa.ses.endpoints.add")}
                    </Button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("sa.ses.table.region")}</th>
                          <th>{t("sa.ses.table.label")}</th>
                          <th>{t("sa.ses.table.status")}</th>
                          <th>{t("sa.ses.table.primary")}</th>
                          <th>{t("sa.ses.table.quota")}</th>
                          <th>{t("sa.ses.table.effectiveQuota")}</th>
                          <th>{t("sa.ses.table.usage")}</th>
                          <th>{t("sa.ses.table.configSet")}</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {endpoints.map((row) => (
                          <tr key={row.id}>
                            <td>
                              <code>{row.region}</code>
                            </td>
                            <td>{row.label}</td>
                            <td>
                              <Badge
                                color={STATUS_COLORS[row.status] || "secondary"}
                                className={
                                  row.status === "blocked"
                                    ? "text-dark"
                                    : undefined
                                }
                              >
                                {t(`sa.ses.status.${row.status}`)}
                              </Badge>
                            </td>
                            <td>
                              {row.is_primary
                                ? t("sa.ses.yes")
                                : t("sa.ses.no")}
                            </td>
                            <td>
                              {formatInt(row.quota_24h)}
                              <span className="text-muted small d-block">
                                {row.max_per_second}/s
                              </span>
                            </td>
                            <td>
                              {formatInt(row.effective_quota_24h)}
                              <span className="text-muted small d-block">
                                {bpsToPercent(row.effective_safety_ceiling_bps)}
                                % · {row.effective_max_per_second}/s
                              </span>
                            </td>
                            <td>{formatUsage(row)}</td>
                            <td>{row.configuration_set || "—"}</td>
                            <td className="text-end">
                              <div className="d-flex flex-wrap gap-1 justify-content-end">
                                <Button
                                  color="soft-primary"
                                  size="sm"
                                  onClick={() => openEdit(row)}
                                >
                                  {t("sa.ses.edit")}
                                </Button>
                                {!row.is_primary && (
                                  <Button
                                    color="soft-danger"
                                    size="sm"
                                    onClick={() => setDeleting(row)}
                                  >
                                    {t("sa.ses.delete")}
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={modalOpen}
        toggle={() => !savingEndpoint && setModalOpen(false)}
        centered
        size="lg"
      >
        <Form onSubmit={submitEndpoint}>
          <ModalHeader toggle={() => !savingEndpoint && setModalOpen(false)}>
            {editing
              ? t("sa.ses.endpoints.editTitle")
              : t("sa.ses.endpoints.addTitle")}
          </ModalHeader>
          <ModalBody>
            {fieldError ? (
              <Alert color="danger" className="py-2">
                {fieldError}
              </Alert>
            ) : null}
            <Row className="g-3">
              <Col md={6}>
                <Label for="sa-ep-region">{t("sa.ses.table.region")}</Label>
                <Input
                  id="sa-ep-region"
                  value={form.region}
                  disabled={Boolean(editing)}
                  invalid={Boolean(fieldError) && !editing}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, region: e.target.value }))
                  }
                  placeholder="us-east-1"
                  required={!editing}
                />
                {editing ? (
                  <p className="text-muted small mb-0 mt-1">
                    {t("sa.ses.endpoints.regionImmutable")}
                  </p>
                ) : null}
              </Col>
              <Col md={6}>
                <Label for="sa-ep-label">{t("sa.ses.table.label")}</Label>
                <Input
                  id="sa-ep-label"
                  value={form.label}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, label: e.target.value }))
                  }
                  required
                  maxLength={120}
                />
              </Col>
              <Col md={4}>
                <Label for="sa-ep-status">{t("sa.ses.table.status")}</Label>
                <Input
                  id="sa-ep-status"
                  type="select"
                  value={form.status}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      status: e.target.value as SaSesEndpointStatus,
                    }))
                  }
                >
                  <option value="active">{t("sa.ses.status.active")}</option>
                  <option value="disabled">{t("sa.ses.status.disabled")}</option>
                  <option value="blocked">{t("sa.ses.status.blocked")}</option>
                </Input>
                {form.status === "blocked" ? (
                  <p className="text-warning small mb-0 mt-1">
                    {t("sa.ses.endpoints.blockedHint")}
                  </p>
                ) : null}
              </Col>
              <Col md={4}>
                <Label for="sa-ep-quota">{t("sa.ses.table.quota")}</Label>
                <Input
                  id="sa-ep-quota"
                  type="number"
                  min={1}
                  value={form.quota_24h}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, quota_24h: e.target.value }))
                  }
                  required
                />
              </Col>
              <Col md={4}>
                <Label for="sa-ep-mps">{t("sa.ses.table.maxPerSecond")}</Label>
                <Input
                  id="sa-ep-mps"
                  type="number"
                  min={1}
                  max={1000}
                  value={form.max_per_second}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, max_per_second: e.target.value }))
                  }
                />
              </Col>
              <Col md={4}>
                <Label for="sa-ep-safety">
                  {t("sa.ses.endpoints.safetyOverride")}
                </Label>
                <Input
                  id="sa-ep-safety"
                  type="number"
                  min={10}
                  max={100}
                  value={form.safety_ceiling_percent}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      safety_ceiling_percent: e.target.value,
                    }))
                  }
                  placeholder={t("sa.ses.endpoints.safetyOverrideHint")}
                />
              </Col>
              <Col md={4}>
                <Label for="sa-ep-config">{t("sa.ses.table.configSet")}</Label>
                <Input
                  id="sa-ep-config"
                  value={form.configuration_set}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      configuration_set: e.target.value,
                    }))
                  }
                  maxLength={128}
                />
              </Col>
              <Col md={2}>
                <Label for="sa-ep-sort">{t("sa.ses.table.sortOrder")}</Label>
                <Input
                  id="sa-ep-sort"
                  type="number"
                  min={0}
                  value={form.sort_order}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, sort_order: e.target.value }))
                  }
                />
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <div className="form-check mb-2">
                  <Input
                    id="sa-ep-primary"
                    type="checkbox"
                    className="form-check-input"
                    checked={form.is_primary}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, is_primary: e.target.checked }))
                    }
                  />
                  <Label
                    for="sa-ep-primary"
                    className="form-check-label"
                  >
                    {t("sa.ses.table.primary")}
                  </Label>
                </div>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              disabled={savingEndpoint}
              onClick={() => setModalOpen(false)}
            >
              {t("sa.ses.cancel")}
            </Button>
            <Button color="primary" type="submit" disabled={savingEndpoint}>
              {savingEndpoint ? <Spinner size="sm" /> : t("sa.ses.save")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal
        isOpen={!!deleting}
        toggle={() => !deletingBusy && setDeleting(null)}
        centered
      >
        <ModalHeader toggle={() => !deletingBusy && setDeleting(null)}>
          {t("sa.ses.endpoints.deleteTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            {t("sa.ses.endpoints.deleteConfirm", {
              region: deleting?.region || "",
              label: deleting?.label || "",
            })}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={deletingBusy}
            onClick={() => setDeleting(null)}
          >
            {t("sa.ses.cancel")}
          </Button>
          <Button
            color="danger"
            disabled={deletingBusy}
            onClick={() => void confirmDelete()}
          >
            {deletingBusy ? <Spinner size="sm" /> : t("sa.ses.deleteConfirm")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SuperAdminSesEndpoints;
